import { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import { AppState, Language, Theme, Product, CartItem, Tab, Banner, Category, QuickLink } from '../types';
import { db, auth } from '../lib/firebase';
import { onAuthStateChanged, User } from 'firebase/auth';
import { collection, onSnapshot, query, doc, orderBy, setDoc, limit, getDocs, startAfter } from 'firebase/firestore';
import { products as staticProducts } from '../data/products';
import ThemePalette from '../components/ThemePalette';
import { getOptimizedImageUrl } from '../utils/imagekit';
import { getProductDisplayPrice } from '../utils/productUtils';

// Local storage caching constants for Firestore free-tier limit (50k reads) optimization
const CACHE_TTL_MS = 20 * 60 * 1000; // 20 minutes cache validity for visitors

function isCacheValid(cacheKey: string): boolean {
  try {
    const timeStr = localStorage.getItem(`${cacheKey}_time`);
    if (!timeStr) return false;
    const time = parseInt(timeStr, 10);
    return (Date.now() - time) < CACHE_TTL_MS;
  } catch {
    return false;
  }
}

function setCacheWithTime(cacheKey: string, data: any) {
  try {
    localStorage.setItem(cacheKey, JSON.stringify(data));
    localStorage.setItem(`${cacheKey}_time`, Date.now().toString());
  } catch (e) {
    console.warn('LocalStorage save error:', e);
  }
}



const AppContext = createContext<AppState | undefined>(undefined);

export function AppProvider({ children }: { children: ReactNode }) {
  const [lang, setLang] = useState<Language>(() => {
    const saved = localStorage.getItem('priyofy_lang') as Language | null;
    if (saved === 'en' || saved === 'bn') return saved;
    return 'bn';
  });
  const [theme, setTheme] = useState<Theme>(() => {
    const saved = localStorage.getItem('priyofy_theme') as Theme | null;
    if (saved === 'dark' || saved === 'light') return saved;
    return 'light';
  });
  
  const [themeColor, setThemeColorState] = useState<string>(() => {
    try {
      return localStorage.getItem('priyofy_theme_color') || '#FF2E93';
    } catch {
      return '#FF2E93';
    }
  });

  const [themeGradient, setThemeGradientState] = useState<string | undefined>(() => {
    try {
      return localStorage.getItem('priyofy_theme_gradient') || undefined;
    } catch {
      return undefined;
    }
  });


  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isEditor, setIsEditor] = useState<boolean>(false);
  const [isOwner, setIsOwner] = useState<boolean>(false);
  const [role, setRole] = useState<'admin' | 'editor' | 'user'>('user');
  const [authLoading, setAuthLoading] = useState<boolean>(true);
  const [initialProductsFetched, setInitialProductsFetched] = useState<boolean>(false);
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);

  // Instant app loading ("চোখের পলকে") - hide splash screen immediately or max 200ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialLoading(false);
    }, 200);
    return () => clearTimeout(timer);
  }, []);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [customProducts, setCustomProducts] = useState<Product[]>(() => {
    try {
      const cached = localStorage.getItem('priyofy_products_cache');
      return cached ? JSON.parse(cached) : [];
    } catch {
      return [];
    }
  });
  const [hasMoreProducts, setHasMoreProducts] = useState(true);
  const [loadingMoreProducts, setLoadingMoreProducts] = useState(false);
  const [lastProductDoc, setLastProductDoc] = useState<any>(null);
  const [banners, setBanners] = useState<Banner[]>(() => {
    try {
      const cached = localStorage.getItem('priyofy_banners_cache');
      return cached ? JSON.parse(cached) : [];
    } catch {
      return [];
    }
  });
  const [categories, setCategories] = useState<Category[]>(() => {
    try {
      const cached = localStorage.getItem('priyofy_categories_cache');
      return cached ? JSON.parse(cached) : [];
    } catch {
      return [];
    }
  });
  const [quickLinks, setQuickLinks] = useState<QuickLink[]>(() => {
    try {
      const cached = localStorage.getItem('priyofy_quicklinks_cache');
      return cached ? JSON.parse(cached) : [];
    } catch {
      return [];
    }
  });
  const [whatsappNumber, setWhatsappNumber] = useState<string>('8801888061036');
  const [supportPhone, setSupportPhone] = useState<string>('+8801888061036');
  const [shippingRates, setShippingRates] = useState<{ insideDhaka: number; outsideDhaka: number; min_order_for_global_free_delivery?: number }>({ insideDhaka: 80, outsideDhaka: 120, min_order_for_global_free_delivery: 0 });

  const setThemeColor = (color: string, gradient?: string) => {
    setThemeColorState(color);
    if (gradient !== undefined) {
      setThemeGradientState(gradient);
    } else {
      setThemeGradientState(undefined); // Clear gradient if only color is provided
    }
    
    try {
      localStorage.setItem('priyofy_theme_color', color);
      if (gradient) {
        localStorage.setItem('priyofy_theme_gradient', gradient);
      } else {
        localStorage.removeItem('priyofy_theme_gradient');
      }
      
      // Persist to Firestore if admin or to user account
      if (isAdmin) {
        // debounce firestore to save network requests when dragging color picker
        if ((window as any)._themeColorTimeout) clearTimeout((window as any)._themeColorTimeout);
        (window as any)._themeColorTimeout = setTimeout(() => {
          const themeDocRef = doc(db, 'store_info', 'theme_config');
          const dataToSave: any = { globalPrimaryColor: color };
          // We always want to sync gradient state, so we update it whether present or not
          dataToSave.globalPrimaryGradient = gradient || null;
          setDoc(themeDocRef, dataToSave, { merge: true }).catch(console.warn);
        }, 500);
      } else {
        localStorage.setItem('priyofy_personal_theme', 'true');
        if (auth.currentUser) {
          if ((window as any)._themeColorUserTimeout) clearTimeout((window as any)._themeColorUserTimeout);
          (window as any)._themeColorUserTimeout = setTimeout(() => {
            const userDocRef = doc(db, 'users', auth.currentUser!.uid);
            const dataToSave: any = { customThemeColor: color };
            dataToSave.customThemeGradient = gradient || null;
            setDoc(userDocRef, dataToSave, { merge: true }).catch(console.warn);
          }, 500);
        }
      }
    } catch (e) {
      console.warn(e);
    }
  };



  useEffect(() => {
    const parseGradientColors = (grad?: string, base: string = '#FF2E93') => {
      if (grad) {
        const matches = grad.match(/#[0-9a-fA-F]{6}|#[0-9a-fA-F]{3}|rgb\([^)]+\)|rgba\([^)]+\)/g);
        if (matches && matches.length >= 2) {
          return [matches[0], matches[1]];
        }
      }
      return [base, base];
    };

    const hexToRgb = (hex: string) => {
      let r = 255, g = 46, b = 147;
      if (hex.startsWith('#')) {
        let cleanHex = hex.replace('#', '');
        if (cleanHex.length === 3) {
          cleanHex = cleanHex.split('').map(c => c + c).join('');
        }
        if (cleanHex.length === 6) {
          r = parseInt(cleanHex.substring(0, 2), 16);
          g = parseInt(cleanHex.substring(2, 4), 16);
          b = parseInt(cleanHex.substring(4, 6), 16);
        }
      }
      return `${r}, ${g}, ${b}`;
    };

    document.documentElement.style.setProperty('--theme-color', themeColor);
    document.documentElement.style.setProperty('--theme-rgb', hexToRgb(themeColor));
    const [c1, c2] = parseGradientColors(themeGradient, themeColor);
    document.documentElement.style.setProperty('--theme-secondary', c2);
    document.documentElement.style.setProperty('--theme-gradient', themeGradient || `linear-gradient(135deg, ${themeColor} 0%, ${themeColor} 100%)`);
  }, [themeColor, themeGradient]);



  // Sync theme globally across users
  useEffect(() => {
    const themeDocRef = doc(db, 'store_info', 'theme_config');
    const hexToRgbGlobal = (hex: string) => {
      let r = 255, g = 46, b = 147;
      if (hex.startsWith('#')) {
        let cleanHex = hex.replace('#', '');
        if (cleanHex.length === 3) {
          cleanHex = cleanHex.split('').map(c => c + c).join('');
        }
        if (cleanHex.length === 6) {
          r = parseInt(cleanHex.substring(0, 2), 16);
          g = parseInt(cleanHex.substring(2, 4), 16);
          b = parseInt(cleanHex.substring(4, 6), 16);
        }
      }
      return `${r}, ${g}, ${b}`;
    };

    const unsubscribe = onSnapshot(themeDocRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        // Ignore global theme update if user has set a personal theme, unless it's the admin
        if (localStorage.getItem('priyofy_personal_theme') === 'true' && !isAdmin) {
          return;
        }

        if (data.globalPrimaryColor) {
          setThemeColorState(data.globalPrimaryColor);
          document.documentElement.style.setProperty('--theme-color', data.globalPrimaryColor);
          document.documentElement.style.setProperty('--theme-rgb', hexToRgbGlobal(data.globalPrimaryColor));
        }
        if (data.globalPrimaryGradient !== undefined) {
          setThemeGradientState(data.globalPrimaryGradient || undefined);
          if (data.globalPrimaryGradient) {
            document.documentElement.style.setProperty('--theme-gradient', data.globalPrimaryGradient);
          } else {
            document.documentElement.style.removeProperty('--theme-gradient');
          }
        }
      }
    }, (error) => {
      console.warn('Failed to listen to theme configuration:', error);
    });
    return () => unsubscribe();
  }, [isAdmin]);

  const [cart, setCart] = useState<CartItem[]>(() => {
    try {
      const saved = localStorage.getItem('priyofy_cart');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem('priyofy_cart', JSON.stringify(cart));
  }, [cart]);
  const [activeTab, setActiveTab] = useState<Tab>('home');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showThemePalette, setShowThemePaletteState] = useState<boolean>(() => {
    try {
      return localStorage.getItem('priyofy_show_theme_palette') === 'true';
    } catch {
      return false;
    }
  });

  const setShowThemePalette = (val: boolean) => {
    setShowThemePaletteState(val);
    try {
      localStorage.setItem('priyofy_show_theme_palette', val ? 'true' : 'false');
    } catch (e) {}
  };

  const [isSoundEnabled, setIsSoundEnabledState] = useState<boolean>(() => {
    try {
      return localStorage.getItem('priyofy_sound') !== 'false';
    } catch {
      return true;
    }
  });

  const [isHapticEnabled, setIsHapticEnabledState] = useState<boolean>(() => {
    try {
      return localStorage.getItem('priyofy_haptic') !== 'false';
    } catch {
      return true;
    }
  });

  const setIsSoundEnabled = (val: boolean) => {
    setIsSoundEnabledState(val);
    try {
      localStorage.setItem('priyofy_sound', val ? 'true' : 'false');
    } catch (e) {
      console.warn(e);
    }
  };

  const setIsHapticEnabled = (val: boolean) => {
    setIsHapticEnabledState(val);
    try {
      localStorage.setItem('priyofy_haptic', val ? 'true' : 'false');
    } catch (e) {
      console.warn(e);
    }
  };

  const [soundVolume, setSoundVolumeState] = useState<number>(() => {
    try {
      const val = localStorage.getItem('priyofy_sound_volume');
      return val ? parseInt(val, 10) : 100;
    } catch {
      return 100;
    }
  });

  const [hapticIntensity, setHapticIntensityState] = useState<number>(() => {
    try {
      const val = localStorage.getItem('priyofy_haptic_strength');
      return val ? parseInt(val, 10) : 100;
    } catch {
      return 100;
    }
  });

  const setSoundVolume = (val: number) => {
    setSoundVolumeState(val);
    try {
      localStorage.setItem('priyofy_sound_volume', val.toString());
    } catch (e) {
      console.warn(e);
    }
  };

  const setHapticIntensity = (val: number) => {
    setHapticIntensityState(val);
    try {
      localStorage.setItem('priyofy_haptic_strength', val.toString());
    } catch (e) {
      console.warn(e);
    }
  };

  // Sync customizable support contacts in real-time
  useEffect(() => {
    const contactsDocRef = doc(db, 'store_info', 'contacts');
    const unsubscribe = onSnapshot(contactsDocRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.whatsapp) {
          setWhatsappNumber(data.whatsapp);
        }
        if (data.phone) {
          setSupportPhone(data.phone);
        }
      }
    }, (error) => {
      console.warn('Failed to listen to support contacts:', error);
    });
    return () => unsubscribe();
  }, []);

  // Sync customizable shipping rates in real-time
  useEffect(() => {
    const shippingDocRef = doc(db, 'store_info', 'shipping');
    const unsubscribe = onSnapshot(shippingDocRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setShippingRates({
          insideDhaka: Number(data.insideDhaka) || 80,
          outsideDhaka: Number(data.outsideDhaka) || 120,
          min_order_for_global_free_delivery: data.min_order_for_global_free_delivery ? Number(data.min_order_for_global_free_delivery) : 0
        });
      }
    }, (error) => {
      console.warn('Failed to listen to shipping rates:', error);
    });
    return () => unsubscribe();
  }, []);

  const updateSupportContacts = async (whatsapp: string, phone: string) => {
    const contactsDocRef = doc(db, 'store_info', 'contacts');
    await setDoc(contactsDocRef, {
      whatsapp: whatsapp.trim(),
      phone: phone.trim()
    }, { merge: true });
  };

  const updateShippingRates = async (inside: number, outside: number, globalMinOrder: number = 0, isGlobalFree: boolean = false, globalLocation: 'all'|'inside_dhaka'|'outside_dhaka' = 'all') => {
    const shippingDocRef = doc(db, 'store_info', 'shipping');
    await setDoc(shippingDocRef, {
      insideDhaka: Number(inside),
      outsideDhaka: Number(outside),
      min_order_for_global_free_delivery: Number(globalMinOrder),
      is_global_free_delivery: isGlobalFree,
      global_free_delivery_location: globalLocation
    }, { merge: true });
  };

  // 1. Firebase Authentication & Custom Role Listener
  useEffect(() => {
    let unsubscribeRole: (() => void) | null = null;

    const unsubscribeAuth = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      
      if (unsubscribeRole) {
        unsubscribeRole();
        unsubscribeRole = null;
      }

      if (!currentUser) {
        setIsAdmin(false);
        setIsEditor(false);
        setIsOwner(false);
        setRole('user');
        setAuthLoading(false);
        return;
      }

      const userEmail = currentUser.email || '';
      const isSystemOwner = userEmail.toLowerCase() === 'priyofy@gmail.com';

      if (isSystemOwner) {
        setIsAdmin(true);
        setIsEditor(false);
        setIsOwner(true);
        setRole('admin');
        setAuthLoading(false);
        return;
      }

      // Subscribe to their user role document in real-time
      const roleDocRef = doc(db, 'user_roles', userEmail.toLowerCase());
      unsubscribeRole = onSnapshot(roleDocRef, (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          const assignedRole = (data.role || 'user') as 'admin' | 'editor' | 'user';
          
          setRole(assignedRole);
          setIsAdmin(assignedRole === 'admin');
          setIsEditor(assignedRole === 'editor');
        } else {
          setRole('user');
          setIsAdmin(false);
          setIsEditor(false);
        }
        setIsOwner(false);
        setAuthLoading(false);
      }, (err) => {
        console.warn('Failed to retrieve real-time role subscription:', err);
        // Fallback to basic user
        setRole('user');
        setIsAdmin(false);
        setIsEditor(false);
        setIsOwner(false);
        setAuthLoading(false);
      });
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeRole) {
        unsubscribeRole();
      }
    };
  }, []);

  // Sync personal theme if user is logged in
  useEffect(() => {
    if (!user || isAdmin) return;

    const userDocRef = doc(db, 'users', user.uid);
    const unsubscribe = onSnapshot(userDocRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.customThemeColor || data.customThemeGradient !== undefined) {
          localStorage.setItem('priyofy_personal_theme', 'true');
          
          if (data.customThemeColor) {
            setThemeColorState(data.customThemeColor);
            document.documentElement.style.setProperty('--theme-color', data.customThemeColor);
            localStorage.setItem('priyofy_theme_color', data.customThemeColor);
          }
          
          if (data.customThemeGradient !== undefined) {
            setThemeGradientState(data.customThemeGradient || undefined);
            if (data.customThemeGradient) {
              document.documentElement.style.setProperty('--theme-gradient', data.customThemeGradient);
              localStorage.setItem('priyofy_theme_gradient', data.customThemeGradient);
            } else {
              document.documentElement.style.removeProperty('--theme-gradient');
              localStorage.removeItem('priyofy_theme_gradient');
            }
          }
        }
      }
    });

    return () => unsubscribe();
  }, [user, isAdmin]);

  // Helper to preload images into browser memory cache for instant rendering
  const preloadImages = (urls: string[]) => {
    urls.forEach((url) => {
      if (url && typeof url === 'string' && url.startsWith('http')) {
        const img = new Image();
        img.src = url;
      }
    });
  };

  // Preload cached product images on initial mount
  useEffect(() => {
    if (customProducts.length > 0) {
      preloadImages(customProducts.map(p => p.image).filter(Boolean));
    }
    if (banners.length > 0) {
      preloadImages(banners.map(b => b.image).filter(Boolean));
    }
  }, []);

  const [catalogVersionTrigger, setCatalogVersionTrigger] = useState(0);

  // 1. Single real-time listener for system/catalog_meta document + Multi-tab localStorage listener
  useEffect(() => {
    const invalidateAndRefresh = () => {
      localStorage.removeItem('priyofy_products_cache_time');
      localStorage.removeItem('priyofy_banners_cache_time');
      localStorage.removeItem('priyofy_categories_cache_time');
      localStorage.removeItem('priyofy_quicklinks_cache_time');
      setCatalogVersionTrigger((prev) => prev + 1);
    };

    // Multi-tab sync across open browser tabs
    const handleStorageChange = (e: StorageEvent) => {
      if (
        e.key === 'priyofy_last_catalog_version' ||
        e.key === 'priyofy_products_cache' ||
        e.key === 'priyofy_products_cache_time'
      ) {
        invalidateAndRefresh();
      }
    };

    const handleCustomInvalidate = () => {
      invalidateAndRefresh();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('priyofy_catalog_invalidated', handleCustomInvalidate);

    const metaRef = doc(db, 'system', 'catalog_meta');
    const unsubscribe = onSnapshot(metaRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        const remoteVersion = Number(data.updatedAt) || 0;
        const localVersion = Number(localStorage.getItem('priyofy_last_catalog_version') || 0);

        if (remoteVersion > localVersion) {
          localStorage.setItem('priyofy_last_catalog_version', remoteVersion.toString());
          invalidateAndRefresh();
        }
      }
    }, (error) => {
      console.warn('Catalog meta sync notice:', error);
    });

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('priyofy_catalog_invalidated', handleCustomInvalidate);
      unsubscribe();
    };
  }, []);

  // 2. Fetch initial Firestore products (Batch of 10)
  useEffect(() => {
    const fetchInitialProducts = async () => {
      // Instant render if cached products exist
      const hasCached = customProducts.length > 0;
      if (hasCached) {
        setInitialProductsFetched(true);
      }

      // If cache is fresh and catalog hasn't invalidated, skip background fetch unless catalog version triggered
      if (isCacheValid('priyofy_products_cache') && hasCached && !isAdmin && catalogVersionTrigger === 0) {
        return;
      }

      try {
        const q = query(collection(db, 'products'), limit(500));
        // Fast 2s timeout race so offline or connection issues don't block
        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('Firestore network timeout')), 2000)
        );
        const snapshot = await (Promise.race([getDocs(q), timeoutPromise]) as Promise<any>);
        const items: Product[] = [];
        snapshot.forEach((docSnap: any) => {
          const data = docSnap.data();
          items.push({
            ...data,
            id: docSnap.id,
            nameEn: data.nameEn || '',
            nameBn: data.nameBn || '',
            descEn: data.descEn || '',
            descBn: data.descBn || '',
            price: Number(data.price) || 0,
            costPrice: data.costPrice !== undefined ? Number(data.costPrice) : 0,
            image: data.image || '',
            categoryEn: data.categoryEn || 'Electronics',
            categoryBn: data.categoryBn || 'ইলেকট্রনিক্স ও গ্যাজেট',
            rating: Number(data.rating) || 5.0,
            relatedMode: data.relatedMode || 'auto',
            relatedIds: data.relatedIds || [],
            sku: data.sku || '',
            regularPrice: Number(data.regularPrice) || 0,
            stockQty: Number(data.stockQty) || 50,
            stock: data.stock !== undefined ? Number(data.stock) : Number(data.stockQty) || 50,
            sizeVar: data.sizeVar || '',
            colorVar: data.colorVar || '',
            additionalImages: data.additionalImages || [],
            colorVariants: data.colorVariants || [],
            brand: data.brand || '',
            skinType: data.skinType || '',
            isDeleted: data.isDeleted || false,
            is_flash_sale: !!data.is_flash_sale,
            flash_sale_end_date: data.flash_sale_end_date || '',
            flashPrice: data.flashPrice !== undefined ? Number(data.flashPrice) : undefined,
            campaignStock: data.campaignStock !== undefined ? Number(data.campaignStock) : undefined,
            soldCount: data.soldCount !== undefined ? Number(data.soldCount) : 0,
            is_verified: !!data.is_verified,
            is_free_delivery: !!data.is_free_delivery,
            free_delivery_location: data.free_delivery_location || 'all',
            free_delivery_min_amount: data.free_delivery_min_amount !== undefined ? Number(data.free_delivery_min_amount) : 0,
          });
        });

        if (items.length > 0) {
          // Check if items changed before triggering re-render to avoid visual flicker
          const itemsChanged = JSON.stringify(items) !== JSON.stringify(customProducts);
          if (itemsChanged || customProducts.length === 0) {
            setCustomProducts(items);
            setCacheWithTime('priyofy_products_cache', items);
            preloadImages(items.map((p) => p.image));
          }
        }

        if (snapshot.docs && snapshot.docs.length > 0) {
          setLastProductDoc(snapshot.docs[snapshot.docs.length - 1]);
        }
        if (snapshot.docs && snapshot.docs.length < 500) {
          setHasMoreProducts(false);
        }
      } catch (error) {
        console.warn('Background products revalidation timed out (using cached products):', error);
      } finally {
        setInitialProductsFetched(true);
      }
    };
    fetchInitialProducts();
  }, [isAdmin, catalogVersionTrigger]);

  const loadMoreProducts = async () => {
    if (!hasMoreProducts || loadingMoreProducts || !lastProductDoc) return;
    setLoadingMoreProducts(true);
    try {
      const q = query(collection(db, 'products'), startAfter(lastProductDoc), limit(500));
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Firestore network timeout')), 2500)
      );
      const snapshot = await (Promise.race([getDocs(q), timeoutPromise]) as Promise<any>);
      const items: Product[] = [];
      snapshot.forEach((docSnap: any) => {
        const data = docSnap.data();
        items.push({
          ...data,
          id: docSnap.id,
          nameEn: data.nameEn || '',
          nameBn: data.nameBn || '',
          descEn: data.descEn || '',
          descBn: data.descBn || '',
          price: Number(data.price) || 0,
          costPrice: data.costPrice !== undefined ? Number(data.costPrice) : 0,
          image: data.image || '',
          categoryEn: data.categoryEn || 'Electronics',
          categoryBn: data.categoryBn || 'ইলেকট্রনিক্স ও গ্যাজেট',
          rating: Number(data.rating) || 5.0,
          relatedMode: data.relatedMode || 'auto',
          relatedIds: data.relatedIds || [],
          sku: data.sku || '',
          regularPrice: Number(data.regularPrice) || 0,
          stockQty: Number(data.stockQty) || 50,
          stock: data.stock !== undefined ? Number(data.stock) : Number(data.stockQty) || 50,
          sizeVar: data.sizeVar || '',
          colorVar: data.colorVar || '',
          additionalImages: data.additionalImages || [],
          colorVariants: data.colorVariants || [],
          brand: data.brand || '',
          skinType: data.skinType || '',
          isDeleted: data.isDeleted || false,
          is_flash_sale: !!data.is_flash_sale,
          flash_sale_end_date: data.flash_sale_end_date || '',
          flashPrice: data.flashPrice !== undefined ? Number(data.flashPrice) : undefined,
          campaignStock: data.campaignStock !== undefined ? Number(data.campaignStock) : undefined,
          soldCount: data.soldCount !== undefined ? Number(data.soldCount) : 0,
          is_verified: !!data.is_verified,
          is_free_delivery: !!data.is_free_delivery,
          free_delivery_location: data.free_delivery_location || 'all',
          free_delivery_min_amount: data.free_delivery_min_amount !== undefined ? Number(data.free_delivery_min_amount) : 0,
        });
      });
      if (items.length > 0) {
        setCustomProducts((prev) => {
          const updated = [...prev, ...items];
          setCacheWithTime('priyofy_products_cache', updated);
          return updated;
        });
        preloadImages(items.map((p) => p.image));
      }
      if (snapshot.docs && snapshot.docs.length > 0) {
        setLastProductDoc(snapshot.docs[snapshot.docs.length - 1]);
      }
      if (snapshot.docs && snapshot.docs.length < 500) {
        setHasMoreProducts(false);
      }
    } catch (error) {
      console.warn('Fetch more products failed:', error);
    }
    setLoadingMoreProducts(false);
  };

  // 3. Dynamic Firestore banners with cache bypass
  useEffect(() => {
    if (isCacheValid('priyofy_banners_cache') && banners.length > 0 && !isAdmin) {
      return;
    }

    const q = query(collection(db, 'banners'), orderBy('order', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const items: Banner[] = [];
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        items.push({
          id: docSnap.id,
          titleEn: data.titleEn || '',
          titleBn: data.titleBn || '',
          subtitleEn: data.subtitleEn || '',
          subtitleBn: data.subtitleBn || '',
          image: data.image || '',
          tagEn: data.tagEn || '',
          tagBn: data.tagBn || '',
          linkUrl: data.linkUrl || '',
          active: data.active !== false,
          order: Number(data.order) || 0,
        });
      });
      if (items.length > 0) {
        setBanners(items);
        setCacheWithTime('priyofy_banners_cache', items);
        preloadImages(items.map((b) => b.image));
      }
    }, (error) => {
      console.warn('Dynamic banners listen failed:', error);
    });
    return () => unsubscribe();
  }, [isAdmin, catalogVersionTrigger]);

  // 4. Dynamic Firestore categories with cache bypass
  useEffect(() => {
    if (isCacheValid('priyofy_categories_cache') && categories.length > 0 && !isAdmin && catalogVersionTrigger === 0) {
      return;
    }

    const q = query(collection(db, 'categories'), orderBy('order', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const items: Category[] = [];
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        items.push({
          id: docSnap.id,
          nameEn: data.nameEn || '',
          nameBn: data.nameBn || '',
          image: data.image || '',
          order: Number(data.order) || 0,
        });
      });
      if (items.length > 0) {
        setCategories(items);
        setCacheWithTime('priyofy_categories_cache', items);
        preloadImages(items.map((c) => c.image));
      }
    }, (error) => {
      console.warn('Dynamic categories listen failed:', error);
    });
    return () => unsubscribe();
  }, [isAdmin, catalogVersionTrigger]);

  // 5. Dynamic Firestore quick links with cache bypass
  useEffect(() => {
    if (isCacheValid('priyofy_quicklinks_cache') && quickLinks.length > 0 && !isAdmin && catalogVersionTrigger === 0) {
      return;
    }

    const q = query(collection(db, 'quick_links'), orderBy('order', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const items: QuickLink[] = [];
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        items.push({
          id: docSnap.id,
          titleEn: data.titleEn || '',
          titleBn: data.titleBn || '',
          image: data.image || '',
          url: data.url || '',
          order: Number(data.order) || 0,
        });
      });
      if (items.length > 0) {
        setQuickLinks(items);
        setCacheWithTime('priyofy_quicklinks_cache', items);
      }
    }, (error) => {
      console.warn('Dynamic quicklinks listen failed:', error);
    });
    return () => unsubscribe();
  }, [isAdmin, catalogVersionTrigger]);


  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
      if (typeof document !== 'undefined' && document.body) {
        document.body.style.backgroundColor = '#121216';
        root.style.backgroundColor = '#121216';
        let meta = document.querySelector('meta[name="theme-color"]');
        if (!meta) {
          meta = document.createElement('meta');
          meta.setAttribute('name', 'theme-color');
          document.head.appendChild(meta);
        }
        meta.setAttribute('content', '#121216');
      }
    } else {
      root.classList.remove('dark');
      if (typeof document !== 'undefined' && document.body) {
        document.body.style.backgroundColor = '#FAF8F6';
        root.style.backgroundColor = '#FAF8F6';
        let meta = document.querySelector('meta[name="theme-color"]');
        if (!meta) {
          meta = document.createElement('meta');
          meta.setAttribute('name', 'theme-color');
          document.head.appendChild(meta);
        }
        meta.setAttribute('content', '#FAF8F6');
      }
    }
    localStorage.setItem('priyofy_theme', theme);
  }, [theme]);

  useEffect(() => {
    localStorage.setItem('priyofy_lang', lang);
  }, [lang]);

  const addToCart = (product: Product) => {
    const { displayPrice, originalPrice } = getProductDisplayPrice(product);
    const effectiveProduct = {
      ...product,
      price: displayPrice,
      regularPrice: product.regularPrice || originalPrice,
    };
    const matched = cart.find(i => i.product.id === product.id);
    setCart(matched 
      ? cart.map(i => i.product.id === product.id ? { ...i, quantity: i.quantity + 1, product: effectiveProduct } : i)
      : [...cart, { product: effectiveProduct, quantity: 1 }]);
  };

  const removeFromCart = (id: string) => setCart(cart.filter(i => i.product.id !== id));
  const updateCartQuantity = (id: string, q: number) => {
    setCart(cart.map(i => i.product.id === id ? { ...i, quantity: Math.max(1, q) } : i));
  };

  const allProducts = useMemo(() => {
    const staticMapped = staticProducts.map(p => {
      let brand = p.brand || 'Priyofy';
      let skinType = p.skinType || 'All';
      if (p.id === 'p1') { brand = 'The Ordinary'; skinType = 'Combination'; }
      if (p.id === 'p2') { brand = 'Priyofy Premium'; skinType = 'Dry'; }
      if (p.id === 'p3') { brand = 'MAC Products'; skinType = 'Sensitive'; }
      if (p.id === 'p4') { brand = 'COSRX'; skinType = 'Oily'; }
      if (p.id === 'p5') { brand = 'Priyofy Premium'; skinType = 'Sensitive'; }
      if (p.id === 'p6') { brand = 'Loreal'; skinType = 'All'; }
      if (p.id === 'p7') { brand = 'COSRX'; skinType = 'Dry'; }
      if (p.id === 'p8') { brand = 'Laneige'; skinType = 'Oily'; }
      return { ...p, brand, skinType };
    });
    let merged = [...staticMapped];
    customProducts.forEach(cp => {
      if (cp.isDeleted) {
        merged = merged.filter(p => p.id !== cp.id);
        return;
      }
      const idx = merged.findIndex(p => p.id === cp.id);
      if (idx !== -1) {
        merged[idx] = {
          ...cp,
          brand: cp.brand || 'Priyofy',
          skinType: cp.skinType || 'All'
        };
      } else {
        merged.push({
          ...cp,
          brand: cp.brand || 'Priyofy',
          skinType: cp.skinType || 'All'
        });
      }
    });
    return merged.filter(p => !p.isDeleted);
  }, [customProducts]);

  return (
    <AppContext.Provider value={{
      lang, theme, themeColor, themeGradient, cart, activeTab, searchQuery, user, isAdmin, isEditor, isOwner, role, authLoading, isInitialLoading, customProducts, allProducts, hasMoreProducts, loadMoreProducts, loadingMoreProducts, banners, categories, quickLinks,
      whatsappNumber, supportPhone, shippingRates, isSoundEnabled, setIsSoundEnabled, soundVolume, setSoundVolume, isHapticEnabled, setIsHapticEnabled, hapticIntensity, setHapticIntensity, setSearchQuery, setActiveTab, setLang, setTheme, setThemeColor, showThemePalette, setShowThemePalette,
      addToCart, 
      removeFromCart, updateCartQuantity, clearCart: () => setCart([]), updateSupportContacts, updateShippingRates,
      selectedProduct, setSelectedProduct
    }}>
      <div className={`${theme} min-h-[100dvh] w-full flex-1 flex flex-col bg-neutral-50 dark:bg-[#0A0A0C] text-neutral-900 dark:text-neutral-100 relative pb-24`}>
        {children}
        <ThemePalette />
      </div>
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext)!;

